// User Logins by Client — in-app stand-in for the Tableau workbook
// "Total User Logins by Client Type".
//
// Cached logins are a daily rollup per user (rpt_user_summarized_login_usage)
// with one counter per client: web_login, goweb_login, desktop_login, mobile,
// telemedicine_login, common_login. This tab unpivots those counters so each
// (facility, user, client) is its own heatmap row, bucketed by week / month /
// quarter / year.
//
// Tableau also shows Suffix, STATUS=ACTIVE, and a NEW_HOUSE_WEB client.
// Those are not separate fields on the cached rollup, so this tab does not
// invent them.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCache } from '../../hooks/useCache';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { useScopeSync } from '../../hooks/useScopeSync';
import { useCurrentScope } from '../../hooks/useCurrentScope';
import { useScope } from '../../contexts/ScopeContext';
import ScopeBar from '../../components/ScopeBar';
import FacilityFilter from '../../components/FacilityFilter';
import GroupFacilitiesModal from '../../components/GroupFacilitiesModal';
import { isTestRecord } from '../../utils/testDataFilter';
import { canonicalVeId } from '../../utils/drilldownAggregate';
import { recordDateYmd } from '../../utils/userAnalytics';
import { downloadObjectsAsXlsx, heatmapToObjects } from '../../utils/exportTableauSheet';
import './Drilldown20.css';
import './UserLoginsByClient.css';

const f = (r, k) => (r?.data || r?.Data || {})[k];

const DATE_PRESETS = [
  { id: 'last_month',    label: 'Last 30 days' },
  { id: 'last_3_months', label: 'Last 3 months' },
  { id: 'last_6_months', label: 'Last 6 months' },
  { id: 'ytd',           label: 'Year to date' },
  { id: 'last_year',     label: 'Last 12 months' },
  { id: 'custom',        label: 'Custom range' },
];

// Order matches how Tableau typically lists the clients on this sheet.
const CLIENT_TYPES = [
  { id: 'desktop',      key: 'desktop_login',      label: 'DESKTOP' },
  { id: 'goweb',        key: 'goweb_login',        label: 'GOWEB' },
  { id: 'mobile',       key: 'mobile',             label: 'MOBILE' },
  { id: 'web',          key: 'web_login',          label: 'WEB' },
  { id: 'telemedicine', key: 'telemedicine_login', label: 'TELEMEDICINE' },
  { id: 'common',       key: 'common_login',       label: 'COMMON' },
];

const CLIENT_ORDER = new Map(CLIENT_TYPES.map((c, i) => [c.id, i]));
const PAGE_SIZE = 250;

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function rangeFromPreset(preset) {
  const end = new Date();
  const start = new Date();
  switch (preset) {
    case 'last_month':    start.setDate(start.getDate() - 30); break;
    case 'last_3_months': start.setMonth(start.getMonth() - 3); break;
    case 'last_6_months': start.setMonth(start.getMonth() - 6); break;
    case 'ytd':           start.setMonth(0); start.setDate(1); break;
    case 'last_year':     start.setFullYear(start.getFullYear() - 1); break;
    default: return { start: null, end: null };
  }
  return { start, end };
}

function toDate(ymdStr) {
  const iso = String(ymdStr || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!iso) return null;
  return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
}

function pad(n) {
  return String(n).padStart(2, '0');
}

/** Sunday-start week, matching the Tableau sheet (Mar 1, 8, 15… in 2026). */
function sundayStart(date) {
  const x = new Date(date.getTime());
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - x.getDay());
  return x;
}

function monthName(date) {
  return date.toLocaleString(undefined, { month: 'short' });
}

function columnForDate(date, granularity) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const quarter = Math.floor((month - 1) / 3) + 1;
  const yearLabel = String(year);
  const monthLabel = `${monthName(date)} ${year}`;

  if (granularity === 'year') {
    return {
      key: yearLabel,
      yearLabel,
      quarterLabel: '',
      monthLabel: '',
      leafLabel: yearLabel,
    };
  }
  if (granularity === 'quarter') {
    return {
      key: `${year}-Q${quarter}`,
      yearLabel,
      quarterLabel: `Q${quarter}`,
      monthLabel: '',
      leafLabel: `Q${quarter}`,
    };
  }
  if (granularity === 'month') {
    return {
      key: `${year}-${pad(month)}`,
      yearLabel,
      quarterLabel: `Q${quarter}`,
      monthLabel,
      leafLabel: monthName(date),
    };
  }
  const start = sundayStart(date);
  return {
    key: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`,
    yearLabel: String(start.getFullYear()),
    quarterLabel: `Q${Math.floor(start.getMonth() / 3) + 1}`,
    monthLabel: `${monthName(start)} ${start.getFullYear()}`,
    leafLabel: String(start.getDate()),
  };
}

function enumerateColumns(startYmd, endYmd, granularity) {
  const start = toDate(startYmd);
  const end = toDate(endYmd);
  if (!start || !end || start > end) return [];
  const seen = new Map();
  const cursor = new Date(start.getTime());
  cursor.setHours(0, 0, 0, 0);
  while (cursor <= end) {
    const col = columnForDate(cursor, granularity);
    if (!seen.has(col.key)) seen.set(col.key, col);
    cursor.setDate(cursor.getDate() + 1);
  }
  const columns = [...seen.values()];
  return columns.map((col, i) => {
    if (i === 0) return { ...col, boundary: 'pane' };
    const prev = columns[i - 1];
    const quarterChanged = Boolean(col.quarterLabel) && col.quarterLabel !== prev.quarterLabel;
    const yearChanged = col.yearLabel !== prev.yearLabel;
    if (yearChanged || quarterChanged) return { ...col, boundary: 'quarter' };
    if (col.monthLabel && col.monthLabel !== prev.monthLabel) return { ...col, boundary: 'month' };
    return { ...col, boundary: '' };
  });
}

function headerBands(columns, field) {
  const bands = [];
  for (const col of columns) {
    const label = col[field];
    if (!label) continue;
    const last = bands[bands.length - 1];
    if (last && last.label === label) last.span += 1;
    else bands.push({ label, span: 1, key: `${field}-${bands.length}-${label}` });
  }
  return bands;
}

function aggregate({ logins, granularity, startYmd, endYmd, enabledClients }) {
  const columns = enumerateColumns(startYmd, endYmd, granularity);
  const colIndex = new Map(columns.map((c, i) => [c.key, i]));
  const rowMap = new Map();

  for (const raw of logins) {
    const day = recordDateYmd(raw);
    const date = toDate(day);
    if (!date) continue;
    const col = columnForDate(date, granularity);
    const ci = colIndex.get(col.key);
    if (ci == null) continue;

    const facId = canonicalVeId(f(raw, 'facility_id'));
    const userId = canonicalVeId(f(raw, 'user_id'));
    if (!userId) continue;

    for (const ct of enabledClients) {
      const n = Number(f(raw, ct.key)) || 0;
      if (n <= 0) continue;
      const rowKey = `${facId || '—'}::${userId}::${ct.id}`;
      let row = rowMap.get(rowKey);
      if (!row) {
        row = {
          rowKey,
          facilityKey: facId || f(raw, 'facility_name') || '—',
          personKey: `${facId || '—'}::${userId}`,
          facilityName: f(raw, 'facility_name') || (facId ? `Facility ${facId}` : '—'),
          displayName: f(raw, 'user_display_name') || `User ${userId}`,
          userType: f(raw, 'user_type') || '',
          speciality: f(raw, 'speciality') || f(raw, 'specialty') || '',
          clientType: ct.label,
          clientId: ct.id,
          buckets: new Array(columns.length).fill(0),
          total: 0,
        };
        rowMap.set(rowKey, row);
      }
      row.buckets[ci] += n;
      row.total += n;
    }
  }

  const rows = [...rowMap.values()];
  rows.sort((a, b) => (
    a.facilityName.localeCompare(b.facilityName)
    || a.displayName.localeCompare(b.displayName)
    || a.personKey.localeCompare(b.personKey)
    || (CLIENT_ORDER.get(a.clientId) ?? 99) - (CLIENT_ORDER.get(b.clientId) ?? 99)
  ));
  return { columns, rows };
}

function spanSizes(rows, keyFn) {
  const sizes = new Array(rows.length).fill(0);
  let i = 0;
  while (i < rows.length) {
    const key = keyFn(rows[i]);
    let j = i + 1;
    while (j < rows.length && keyFn(rows[j]) === key) j += 1;
    sizes[i] = j - i;
    i = j;
  }
  return sizes;
}

function boundaryClass(boundary) {
  return boundary ? `ulc-group ulc-group-${boundary}` : '';
}

function HeatCell({ value, rowMax, boundary }) {
  const cls = ['dd-cell', boundaryClass(boundary), value ? '' : 'dd-cell-empty'].filter(Boolean).join(' ');
  if (!value) return <td className={cls}>·</td>;
  const pct = rowMax > 0 ? Math.min(1, value / rowMax) : 0;
  const alpha = 0.15 + pct * 0.75;
  return (
    <td
      className={cls}
      style={{
        backgroundColor: `rgba(15, 118, 110, ${alpha.toFixed(3)})`,
        color: pct > 0.55 ? '#f8fafc' : '#134e4a',
      }}
    >
      {value.toLocaleString()}
    </td>
  );
}

function UserLoginsByClient() {
  const cache = useCache();
  const { customers, resolveCustomerFacilityIds } = useCustomerFilter();
  const { clearScope: clearGlobalScope } = useScope();

  const [granularity, setGranularity] = useState('week');
  const [dateRangePreset, setDateRangePreset] = useState('last_3_months');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [removeTestData, setRemoveTestData] = useState(true);
  const [clientIds, setClientIds] = useState(() => CLIENT_TYPES.map(c => c.id));
  const [nameQuery, setNameQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const [selectedFacilityIds, setSelectedFacilityIds] = useState(() => new Set());
  const [activeCustomerId, setActiveCustomerId] = useState('');
  const [activeFilterId, setActiveFilterId] = useState('');
  const [savedFilters, setSavedFilters] = useState([]);
  const [showFacilityFilter, setShowFacilityFilter] = useState(false);
  const [showScopeFacilities, setShowScopeFacilities] = useState(false);

  const [hierarchy, setHierarchy] = useState([]);
  const [logins, setLogins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);

  const toggleClient = useCallback((id) => {
    setClientIds((prev) => {
      if (prev.includes(id)) {
        if (prev.length <= 1) return prev;
        return prev.filter(x => x !== id);
      }
      const next = [...prev, id];
      return CLIENT_TYPES.map(c => c.id).filter(x => next.includes(x));
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await cache.getCachedFacilityHierarchy();
        if (r?.success) setHierarchy(r.hierarchy || []);
      } catch { /* optional */ }
      try {
        const r = await cache.getSavedFilters();
        if (r?.filters) setSavedFilters(r.filters);
      } catch { /* no filters yet */ }
    })();
    const { start, end } = rangeFromPreset('last_3_months');
    setStartDate(ymd(start));
    setEndDate(ymd(end));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dateRangePreset !== 'custom') {
      const { start, end } = rangeFromPreset(dateRangePreset);
      if (start && end) {
        setStartDate(ymd(start));
        setEndDate(ymd(end));
      }
    }
  }, [dateRangePreset]);

  const load = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError('');
    setHasLoaded(false);
    setVisibleCount(PAGE_SIZE);
    try {
      setProgress({ current: 0, total: 1, label: 'Logins' });
      const res = await cache.getCachedData('logins', startDate, endDate);
      setLogins(res?.success ? (res.records || []) : []);
      setProgress({ current: 1, total: 1, label: 'Done' });
      setHasLoaded(true);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Load failed.');
    } finally {
      setLoading(false);
      setTimeout(() => setProgress(null), 1200);
    }
  }, [cache, startDate, endDate]);

  const { setScopeFromCustomer, setScopeFromFilter } = useScopeSync({
    customers,
    savedFilters,
    resolveCustomerFacilityIds,
    setSelectedFacilityIds,
    setActiveCustomerId,
    setActiveFilterId,
    loadData: load,
    ready: Boolean(startDate && endDate),
  });

  const currentScope = useCurrentScope({
    activeCustomerId,
    activeFilterId,
    selectedFacilityIds,
    customers,
    savedFilters,
    setActiveCustomerId,
    setActiveFilterId,
    setSelectedFacilityIds,
    clearGlobalScope,
  });

  const applySavedFilter = (filterId) => {
    setActiveFilterId(filterId);
    setActiveCustomerId('');
    if (!filterId) setSelectedFacilityIds(new Set());
    else {
      const sf = savedFilters.find(s => String(s.id) === String(filterId));
      if (sf?.facilityIds) setSelectedFacilityIds(new Set(sf.facilityIds.map(String)));
    }
    setScopeFromFilter(filterId);
  };

  const applyCustomerFilter = async (customerId) => {
    setActiveCustomerId(customerId);
    setActiveFilterId('');
    if (!customerId) setSelectedFacilityIds(new Set());
    else {
      const ids = await resolveCustomerFacilityIds(customerId);
      setSelectedFacilityIds(new Set(ids.map(String)));
    }
    setScopeFromCustomer(customerId);
  };

  const dataFacilityIds = useMemo(() => {
    const ids = new Set();
    for (const r of logins) {
      const fac = canonicalVeId(f(r, 'facility_id'));
      if (fac) ids.add(fac);
    }
    return ids;
  }, [logins]);

  const filteredLogins = useMemo(() => {
    let rows = logins;
    if (removeTestData) rows = rows.filter(r => !isTestRecord(r));
    if (selectedFacilityIds.size > 0) {
      const facIds = new Set([...selectedFacilityIds].map(canonicalVeId));
      rows = rows.filter(r => {
        const fac = canonicalVeId(f(r, 'facility_id'));
        return fac && facIds.has(fac);
      });
    }
    return rows;
  }, [logins, removeTestData, selectedFacilityIds]);

  const enabledClients = useMemo(
    () => CLIENT_TYPES.filter(c => clientIds.includes(c.id)),
    [clientIds]
  );

  const result = useMemo(() => {
    if (!hasLoaded) return { columns: [], rows: [] };
    return aggregate({
      logins: filteredLogins,
      granularity,
      startYmd: startDate,
      endYmd: endDate,
      enabledClients,
    });
  }, [hasLoaded, filteredLogins, granularity, startDate, endDate, enabledClients]);

  const searchedRows = useMemo(() => {
    const q = nameQuery.trim().toLowerCase();
    if (!q) return result.rows;
    return result.rows.filter(r => (
      r.displayName.toLowerCase().includes(q)
      || r.facilityName.toLowerCase().includes(q)
      || r.userType.toLowerCase().includes(q)
      || r.speciality.toLowerCase().includes(q)
      || r.clientType.toLowerCase().includes(q)
    ));
  }, [result.rows, nameQuery]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [nameQuery, granularity, clientIds, hasLoaded]);

  const visibleRows = searchedRows.slice(0, visibleCount);
  const facilitySpans = spanSizes(visibleRows, r => r.facilityKey);
  const personSpans = spanSizes(visibleRows, r => r.personKey);
  // Grouping bands sit above the leaf (week-day / month / quarter / year).
  // The leaf already names the finest period, so that level is not repeated.
  const showYear = granularity !== 'year';
  const showQuarter = granularity === 'week' || granularity === 'month';
  const showMonth = granularity === 'week';
  const yearBands = showYear ? headerBands(result.columns, 'yearLabel') : [];
  const quarterBands = showQuarter ? headerBands(result.columns, 'quarterLabel') : [];
  const monthBands = showMonth ? headerBands(result.columns, 'monthLabel') : [];
  const idRowSpan = 1 + Number(showYear) + Number(showQuarter) + Number(showMonth);

  return (
    <div className="dd20 ulc">
      <p className="dd20-intro">
        Total user logins split by client, in the spirit of the Tableau
        <em> Total User Logins by Client Type</em> workbook. Each row is one
        person at one facility on one client (Desktop, GoWeb, Mobile, Web,
        Telemedicine, Common). Weeks start on Sunday, the same way that sheet groups dates.
      </p>

      <div className="dd20-controls">
        <div className="dd20-field dd20-field-metrics">
          <span className="dd20-field-label" id="ulc-client-label">Client type</span>
          <div className="dd20-metric-list" role="group" aria-labelledby="ulc-client-label">
            {CLIENT_TYPES.map(c => (
              <label
                key={c.id}
                className={[
                  'dd20-metric-option',
                  clientIds.includes(c.id) ? 'dd20-metric-option-on' : '',
                ].filter(Boolean).join(' ')}
              >
                <input
                  type="checkbox"
                  checked={clientIds.includes(c.id)}
                  disabled={loading}
                  onChange={() => toggleClient(c.id)}
                />
                <span>{c.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="dd20-field">
          <label htmlFor="ulc-period">Period</label>
          <select
            id="ulc-period"
            value={granularity}
            onChange={e => setGranularity(e.target.value)}
          >
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
            <option value="quarter">Quarterly</option>
            <option value="year">Yearly</option>
          </select>
        </div>

        <div className="dd20-field">
          <label htmlFor="ulc-date">Date range</label>
          <select
            id="ulc-date"
            value={dateRangePreset}
            onChange={e => setDateRangePreset(e.target.value)}
            disabled={loading}
          >
            {DATE_PRESETS.map(p => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </div>

        {dateRangePreset === 'custom' && (
          <>
            <div className="dd20-field">
              <label htmlFor="ulc-start">Start</label>
              <input id="ulc-start" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} disabled={loading} />
            </div>
            <div className="dd20-field">
              <label htmlFor="ulc-end">End</label>
              <input id="ulc-end" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} disabled={loading} />
            </div>
          </>
        )}

        <div className="dd20-field">
          <label htmlFor="ulc-search">Find</label>
          <input
            id="ulc-search"
            type="search"
            placeholder="Name, facility, specialty…"
            value={nameQuery}
            onChange={e => setNameQuery(e.target.value)}
          />
        </div>

        {savedFilters.length > 0 && (
          <div className="dd20-field">
            <label htmlFor="ulc-saved-filter">Saved filter</label>
            <select
              id="ulc-saved-filter"
              value={activeFilterId}
              onChange={e => applySavedFilter(e.target.value)}
              disabled={loading}
            >
              <option value="">None (show all)</option>
              {savedFilters.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.facilityIds?.length || 0})
                </option>
              ))}
            </select>
          </div>
        )}

        {customers.length > 0 && (
          <div className="dd20-field">
            <label htmlFor="ulc-customer">Customer</label>
            <select
              id="ulc-customer"
              value={activeCustomerId}
              onChange={e => applyCustomerFilter(e.target.value)}
              disabled={loading}
            >
              <option value="">None (show all)</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>
                  {c._depth > 0 ? '— ' : ''}{c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <button
          type="button"
          className="dd20-filter-btn"
          onClick={() => setShowFacilityFilter(true)}
          disabled={loading}
        >
          Custom filter
          {selectedFacilityIds.size > 0 ? ` (${selectedFacilityIds.size})` : ''}
        </button>

        <label className="dd20-check">
          <input
            type="checkbox"
            checked={removeTestData}
            onChange={e => setRemoveTestData(e.target.checked)}
          />
          Remove test data
        </label>

        <button
          type="button"
          className="dd20-load"
          onClick={load}
          disabled={loading || !startDate || !endDate}
        >
          {loading ? 'Loading…' : 'Load data'}
        </button>
      </div>

      <ScopeBar
        scope={currentScope}
        onViewFacilities={
          currentScope.kind !== 'none' && hierarchy?.length && selectedFacilityIds.size
            ? () => setShowScopeFacilities(true)
            : null
        }
      />

      {showScopeFacilities && currentScope.kind !== 'none' && (
        <GroupFacilitiesModal
          groupName={currentScope.name}
          hierarchy={hierarchy}
          facilityIds={Array.from(selectedFacilityIds)}
          onClose={() => setShowScopeFacilities(false)}
        />
      )}

      {progress && (
        <div className="dd20-progress">{progress.label} ({progress.current}/{progress.total})</div>
      )}
      {error && <div className="dd20-error">{error}</div>}
      {!hasLoaded && !loading && (
        <div className="dd20-empty-first">
          Pick a customer, saved filter, or facility set if you want to narrow the
          sheet, then click <strong>Load data</strong>.
        </div>
      )}

      {hasLoaded && (
        <>
          <div className="dd20-section-header">
            <div>
              <h3>Total Logins by Client Type</h3>
              <p className="dd20-view-hint">
                {searchedRows.length.toLocaleString()} user × client rows
                {searchedRows.length !== result.rows.length
                  ? ` (filtered from ${result.rows.length.toLocaleString()})`
                  : ''}
                {' '}· {filteredLogins.length.toLocaleString()} daily login rollups
              </p>
            </div>
            <div className="dd20-section-actions">
              <button
                type="button"
                className="dd20-export-btn"
                disabled={!searchedRows.length}
                title="Download all filtered rows as Excel (not just the visible page)"
                onClick={() => {
                  const { rows: exportRows, headers } = heatmapToObjects(
                    { columns: result.columns, rows: searchedRows },
                    {
                      idColumns: [
                        { key: 'facilityName', header: 'Facility' },
                        { key: 'displayName', header: 'Display name' },
                        { key: 'userType', header: 'User type' },
                        { key: 'speciality', header: 'Specialty' },
                        { key: 'clientType', header: 'Client' },
                      ],
                    }
                  );
                  downloadObjectsAsXlsx(exportRows, {
                    fileStem: 'user_logins_by_client',
                    sheetName: 'Logins by Client',
                    headers,
                    startDate,
                    endDate,
                  });
                }}
              >
                Export Excel
              </button>
            </div>
          </div>

          {visibleRows.length === 0 ? (
            <div className="dd-empty">No logins match this window and client selection.</div>
          ) : (
            <div className="dd-table-scroll">
              <table className="dd-heatmap ulc-table">
                <thead>
                  <tr>
                    <th className="ulc-id ulc-lock ulc-lock-1" rowSpan={idRowSpan}>Facility</th>
                    <th className="ulc-id ulc-lock ulc-lock-2" rowSpan={idRowSpan}>Display name</th>
                    <th className="ulc-id ulc-lock ulc-lock-3" rowSpan={idRowSpan}>User type</th>
                    <th className="ulc-id ulc-lock ulc-lock-4" rowSpan={idRowSpan}>Specialty</th>
                    <th className="ulc-id ulc-lock ulc-lock-5" rowSpan={idRowSpan}>Client</th>
                    {showYear
                      ? yearBands.map(b => (
                        <th key={b.key} className="ulc-band" colSpan={b.span}>{b.label}</th>
                      ))
                      : result.columns.map(c => (
                        <th key={c.key} className={`dd-col-header ${boundaryClass(c.boundary)}`}>{c.leafLabel}</th>
                      ))}
                    <th className="dd-total-header ulc-pane" rowSpan={idRowSpan}>Grand total</th>
                  </tr>
                  {showQuarter && (
                    <tr>
                      {quarterBands.map(b => (
                        <th key={b.key} className="ulc-band ulc-band-sub" colSpan={b.span}>{b.label}</th>
                      ))}
                    </tr>
                  )}
                  {showMonth && (
                    <tr>
                      {monthBands.map(b => (
                        <th key={b.key} className="ulc-band ulc-band-sub" colSpan={b.span}>{b.label}</th>
                      ))}
                    </tr>
                  )}
                  {showYear && (
                    <tr>
                      {result.columns.map(c => (
                        <th key={c.key} className={`dd-col-header ${boundaryClass(c.boundary)}`}>{c.leafLabel}</th>
                      ))}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {visibleRows.map((r, idx) => {
                    const rowMax = Math.max(0, ...r.buckets);
                    const facilitySpan = facilitySpans[idx];
                    const personSpan = personSpans[idx];
                    return (
                      <tr key={r.rowKey} className="dd-row">
                        {facilitySpan > 0 && (
                          <td className="ulc-id ulc-merged ulc-lock ulc-lock-1" rowSpan={facilitySpan} title={r.facilityName}>
                            {r.facilityName}
                          </td>
                        )}
                        {personSpan > 0 && (
                          <td className="ulc-id ulc-merged ulc-lock ulc-lock-2" rowSpan={personSpan} title={r.displayName}>
                            {r.displayName}
                          </td>
                        )}
                        {personSpan > 0 && (
                          <td className="ulc-id ulc-merged ulc-lock ulc-lock-3" rowSpan={personSpan} title={r.userType || ''}>
                            {r.userType || '—'}
                          </td>
                        )}
                        {personSpan > 0 && (
                          <td className="ulc-id ulc-merged ulc-lock ulc-lock-4" rowSpan={personSpan} title={r.speciality || ''}>
                            {r.speciality || '—'}
                          </td>
                        )}
                        <td className="ulc-client ulc-lock ulc-lock-5">{r.clientType}</td>
                        {r.buckets.map((n, i) => (
                          <HeatCell
                            key={result.columns[i].key}
                            value={n}
                            rowMax={rowMax}
                            boundary={result.columns[i].boundary}
                          />
                        ))}
                        <td className="dd-row-total ulc-pane">{r.total.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {searchedRows.length > visibleRows.length && (
            <button
              type="button"
              className="dd20-filter-btn ulc-more"
              onClick={() => setVisibleCount(n => n + PAGE_SIZE)}
            >
              Show more ({visibleRows.length.toLocaleString()} of {searchedRows.length.toLocaleString()})
            </button>
          )}
        </>
      )}

      <details className="dd20-gaps">
        <summary>What this sheet does not match yet</summary>
        <ul>
          <li>
            <strong>Suffix</strong> — Tableau reads <code>rpt_user.Suffix</code>.
            The cached daily login rollup does not include it.
          </li>
          <li>
            <strong>STATUS = ACTIVE</strong> — not on the login rollup, so inactive
            users who still have login counts in the window stay on the sheet.
          </li>
          <li>
            <strong>NEW_HOUSE_WEB</strong> — not a separate counter. Those logins
            are inside Web or Common if the VitalEngine summary folded them there.
          </li>
        </ul>
      </details>

      {showFacilityFilter && (
        <FacilityFilter
          hierarchy={hierarchy}
          availableFacilityIds={dataFacilityIds}
          selectedFacilityIds={selectedFacilityIds}
          onSelectionChange={(ids) => {
            setSelectedFacilityIds(ids);
            setActiveCustomerId('');
            setActiveFilterId('');
            clearGlobalScope();
          }}
          onClose={() => setShowFacilityFilter(false)}
        />
      )}
    </div>
  );
}

export default UserLoginsByClient;
